/**
 * Zod schema for the `eventLocations` collection.
 *
 * Canonical record for any place that may be referenced by other entities
 * in the event (the venue, sessions, travel touchpoints) or exist as a
 * standalone point of interest (a hotel, a ski rental shop, a restaurant
 * for a free evening). Every map pin in the event is one of these
 * documents.
 *
 * Reference model (post-categories-refactor):
 *   - Event.venueLocationId points at one location
 *   - Session.locationId points at one location (or null = no map context)
 *   - TravelItem.pickupLocationId / .dropoffLocationId reference zero, one,
 *     or two locations per kind
 *
 * EventGroups deliberately do not carry a location — they're people sets,
 * not place entities. Removed in the ADR-114 follow-up cleanup.
 *
 * Coordinates are nullable. A label-only location (e.g. "Conference Room
 * 203" with no map pin) is a valid record. Only locations with non-null
 * coordinates render on the map; the rest serve as descriptive labels and
 * still benefit from the reverse-index ("which sessions are in Room 203?").
 *
 * Color lives on the location. The admin LocationPicker sets a smart
 * default at creation time based on context (category color when
 * created from SessionForm, etc.) and the organizer can override.
 *
 * Filtering on the mobile map is by core data-model entity (venue /
 * sessions / groups / travel / other) via reverse-lookup over the
 * consuming collections, NOT via a category field on the location.
 * Categories were removed because they duplicated the role of session
 * types and forced a single classification onto pins that may serve
 * multiple consumers (a restaurant referenced by both a session and a
 * travel item has no single "category").
 */

import { z } from 'zod';

export const eventLocationDocumentSchema = z.object({
  eventId: z.string(),
  label: z.string().min(1),
  /**
   * Nullable. Set when the location has a map pin; null when it's a
   * label-only entry (a room name, a generic area).
   */
  coordinates: z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
  }).nullable(),
  /**
   * Street line auto-filled from the MapKit Place's `fullThoroughfare`
   * on creation. Independently editable. Null for label-only locations
   * or pins placed manually without a Place lookup.
   */
  address: z.string().nullable(),
  /**
   * City / locality. Auto-filled from MapKit's `Place.locality` when the
   * organizer picks a venue from search; independently editable on the
   * Location form. Null if MapKit didn't return one (rare) or for
   * label-only locations.
   */
  city: z.string().nullable(),
  /**
   * State / province / region. Auto-filled from MapKit's
   * `Place.administrativeArea`; independently editable.
   */
  stateOrRegion: z.string().nullable(),
  /** Country. Auto-filled from MapKit's `Place.country`; independently editable. */
  country: z.string().nullable(),
  /**
   * Hex color used to render the pin and the color dot in the locations
   * list. The LocationPicker sets a smart default at creation time;
   * organizer can override at any time. Required (no null sentinel).
   */
  color: z.string(),
  /**
   * True when this location was created from a MapKit Place pick AND has
   * not been manually edited since. Surfaced in the LocationPicker as a
   * "Verified — Maps" chip vs a "Custom" chip so organizers can see at a
   * glance whether the address parts are backed by Apple's database or
   * authored by hand. Flips to false on any field edit (drift detected).
   *
   * Custom locations are first-class — they save, render pins on the
   * map with the configured color, work in every consumer. Verification
   * is metadata about provenance, not a gating flag.
   */
  verified: z.boolean(),
});
